import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Send, FileText, Mail, Receipt, Image as ImageIcon, FileDown } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * All the admin toolbar buttons displayed at the top-right of the dashboard.
 * Extracted from Admin.jsx to keep that file focused on data + tabs.
 */
export default function AdminHeaderActions({ schedulerStatus, onReload }) {
  const runReminders = async () => {
    if (!window.confirm("Envoyer un email de rappel à tous les participants des événements de demain ?")) return;
    try {
      const { data } = await api.post("/admin/send-reminders");
      toast.success(`${data.sent} email(s) envoyé(s) pour ${data.events || 0} événement(s)`);
      onReload?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const runWeekly = async () => {
    if (!window.confirm("Envoyer le récap hebdomadaire à votre adresse admin maintenant ?")) return;
    try {
      const { data } = await api.post("/admin/send-weekly-summary");
      toast.success(`Récap envoyé à ${data.recipient}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const runPosDaily = async () => {
    if (!window.confirm("Envoyer le récap POS du jour à votre adresse admin maintenant ?")) return;
    try {
      const { data } = await api.post("/admin/send-pos-daily-report");
      if (data.skipped) {
        toast.info(`Récap non envoyé : ${data.reason || "aucune activité"}`);
      } else {
        toast.success(`Récap POS envoyé (${data.revenue?.toFixed?.(0) || 0} €, ${data.orders_count} table(s))`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const runDailyDigest = async () => {
    if (!window.confirm("Envoyer le récap quotidien de livraison des emails maintenant ?")) return;
    try {
      const { data } = await api.post("/admin/send-daily-delivery-digest");
      if (data.skipped) {
        toast.info(`Récap non envoyé : ${data.reason || "aucune activité"}`);
      } else {
        toast.success(`Récap quotidien envoyé à ${data.recipient}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const downloadQrSheet = async () => {
    try {
      const res = await api.get("/admin/tables/qr-sheet.pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "saveurs-qr-tables.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Fiche QR téléchargée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur téléchargement");
    }
  };

  return (
    <div className="flex flex-col items-end gap-2">
      {schedulerStatus?.next_run && (
        <div className="text-[11px] text-muted-foreground" data-testid="scheduler-info">
          Prochain envoi auto : {new Date(schedulerStatus.next_run).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
        </div>
      )}
      <Button variant="outline" className="rounded-full" onClick={runReminders} data-testid="send-reminders-btn">
        <Send className="w-4 h-4 mr-2" /> Envoyer rappels J-1
      </Button>
      <Button variant="ghost" size="sm" className="rounded-full text-xs text-muted-foreground hover:text-primary" onClick={runWeekly} data-testid="send-weekly-summary-btn">
        <FileText className="w-3.5 h-3.5 mr-1.5" /> Récap hebdo (test)
      </Button>
      <Button variant="ghost" size="sm" className="rounded-full text-xs text-muted-foreground hover:text-primary" onClick={runPosDaily} data-testid="send-pos-daily-report-btn">
        <Receipt className="w-3.5 h-3.5 mr-1.5" /> Récap POS (test)
      </Button>
      <Button variant="ghost" size="sm" className="rounded-full text-xs text-muted-foreground hover:text-primary" onClick={runDailyDigest} data-testid="send-daily-digest-btn">
        <Mail className="w-3.5 h-3.5 mr-1.5" /> Récap livraison (test)
      </Button>
      <Button asChild variant="outline" className="rounded-full" data-testid="menu-editor-link">
        <Link to="/admin/carte">
          <ImageIcon className="w-4 h-4 mr-2" /> Éditer la carte
        </Link>
      </Button>
      <Button asChild variant="outline" className="rounded-full" data-testid="ingredients-editor-link">
        <Link to="/admin/ingredients">
          🧪 Ingrédients & recettes
        </Link>
      </Button>
      <Button asChild variant="outline" className="rounded-full" data-testid="content-editor-link">
        <Link to="/admin/contenu">
          <FileText className="w-4 h-4 mr-2" /> Contenus du site
        </Link>
      </Button>
      <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="pos-link">
        <Link to="/pos">
          <Receipt className="w-4 h-4 mr-2" /> Ouvrir le POS
        </Link>
      </Button>
      <Button variant="outline" className="rounded-full" onClick={downloadQrSheet} data-testid="qr-sheet-btn" title="Télécharger la fiche A4 avec les 20 QR codes tables">
        <FileDown className="w-4 h-4 mr-2" /> QR tables (PDF)
      </Button>
    </div>
  );
}
